import React from 'react';
import { ListingEntry, TableColumnType } from 'components';
import { AuthContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import { getLocalTimeZone } from 'utils';
import { StatusEntry } from '../../../components';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';
import {
  CommonInnerTableProps,
  ListingTableData,
  TableRenderFn,
} from '../types';
import { listingAgentNeedsAction } from '../utils';
import { renderCoopContactEntry, buildTableComponents } from './tableUtils';
import {
  ActionsEntry,
  Covid19Entry,
  FeedbackEntry,
  StyledInnerTable,
  StyledMobileColumn,
} from './components';
import DotOrCheckbox from './components/DotOrCheckbox';
import { useWriteCalendarHover } from '../ShowingsCalendarHoverContext';

const ListingTable: React.FC<CommonInnerTableProps<ListingTableData>> = ({
  tableKey,
  showings,
  selectedCovidAppt,
  setSelectedCovidAppt,
  openAppointmentModal,
  isCovidSignWorking,
}) => {
  const { permissionsMap, listing } = useShowingsCalendarContext();
  const { setHoveredAppointment } = useWriteCalendarHover();
  const { auth } = React.useContext(AuthContext);
  const { md } = useBreakpoint();

  const renderListingEntry: TableRenderFn<ListingTableData[number]> = (
    _,
    {
      id,
      status,
      time,
      timeEnd,
      listing: { line1, line2, thumbnail, timezone },
      listingContactTimeChange,
      listingId,
      passed,
    },
  ) => {
    const needsAction = listingAgentNeedsAction({
      status,
      listingContactTimeChange,
    });

    return (
      <ListingEntry
        listingEntryExtraContent={
          <DotOrCheckbox
            appointmentId={id}
            dotVisible={needsAction}
            status={status}
            passed={passed}
          />
        }
        listingId={listingId}
        time={time}
        timeEnd={timeEnd}
        timezone={getLocalTimeZone(auth)}
        line1={line1}
        line2={line2}
        thumbnail={thumbnail}
        needsAction={needsAction}
        status={status}
        data-cy="listing-tab-entry"
      />
    );
  };

  const renderStatusEntry: TableRenderFn<ListingTableData[number]> = (
    _,
    { displayStatus, type },
  ) => (
    <StatusEntry isBuyer={false} displayStatus={displayStatus} type={type} />
  );

  const renderSecondaryActions: TableRenderFn<ListingTableData[number]> = (
    _,
    {
      id,
      covid19Overridden,
      covid19RequestedAck,
      covid19Signed,
      feedbackCollected,
      orgHasCovidFlag,
      passed,
      signature,
      status,
      timeEnd,
      type,
      listingId,
    },
    i,
    opts,
  ) => (
    <>
      {orgHasCovidFlag && (
        <Covid19Entry
          isBuyer={false}
          covid19RequestedAck={covid19RequestedAck}
          covid19Signed={covid19Signed}
          covid19Overridden={covid19Overridden}
          signatureType={signature?.template ? 'template' : 'pdf'}
          status={status}
          timeEnd={timeEnd}
          onSignPrompt={() => openAppointmentModal(id, listingId, 'covid')}
          // @ts-ignore BuildJsonType does not go deep enough to support this
          onSignatureView={() => setSelectedCovidAppt(showings[i])}
          isMobile={opts?.isMobile}
          loading={isCovidSignWorking && selectedCovidAppt?.id === id}
        />
      )}
      <FeedbackEntry
        feedbackCollected={feedbackCollected}
        isBuyer={false}
        onClick={() => openAppointmentModal(id, listingId, 'feedback')}
        passed={passed}
        status={status}
        type={type}
        isMobile={opts?.isMobile}
      />
    </>
  );

  const renderPrimaryActions: TableRenderFn<ListingTableData[number]> = (
    _,
    appointment,
    i,
    opts,
  ) => {
    const isListingAgent = appointment.listing?.reps.some(
      repId => repId === auth?.id,
    );
    return (
      <ActionsEntry
        isBuyer={false}
        // @ts-expect-error: bad type
        appointment={appointment}
        permissions={permissionsMap[appointment.id]}
        canViewDetails={
          auth.roles[auth.role] >= auth.roles?.BRANCH_ADMIN || isListingAgent
        }
        isMobile={opts?.isMobile}
      />
    );
  };

  const renderMobile: TableRenderFn<ListingTableData[number]> = (
    _,
    appointment,
    i,
  ) => (
    <StyledMobileColumn>
      {[
        renderStatusEntry(_, appointment, i, { isMobile: true }),
        renderListingEntry(_, appointment, i, { isMobile: true }),
        renderCoopContactEntry(_, appointment, i, { isMobile: true }),
        renderPrimaryActions(_, appointment, i, { isMobile: true }),
        renderSecondaryActions(_, appointment, i, { isMobile: true }),
      ]}
    </StyledMobileColumn>
  );

  const columns: TableColumnType<ListingTableData[number]>[] = md
    ? // Desktop view
      [
        {
          key: 'geo',
          dataIndex: '',
          width: 275,
          render: renderListingEntry,
        },
        {
          key: 'status',
          dataIndex: '',
          width: 250,
          render: renderStatusEntry,
        },
        {
          key: 'coop',
          dataIndex: 'coop',
          width: 250,
          render: renderCoopContactEntry,
        },
        {
          key: 'secondaryActions',
          dataIndex: '',
          width: 200,
          render: renderSecondaryActions,
        },
        {
          key: 'primaryActions',
          dataIndex: '',
          render: renderPrimaryActions,
        },
      ]
    : // Mobile View
      [
        {
          key: 'mobile',
          dataIndex: '',
          render: renderMobile,
        },
      ];

  return (
    <StyledInnerTable
      components={buildTableComponents({ md })}
      key={tableKey}
      columns={columns}
      rowKey={record => record.id}
      showHeader={false}
      dataSource={showings}
      pagination={false}
      onRow={({ id, listingId }) => ({
        onClick: () => openAppointmentModal(id, listingId, 'history'),
        onMouseEnter: () => setHoveredAppointment(id),
        onMouseLeave: () => setHoveredAppointment(null),
      })}
      data-cy="listing-table"
    />
  );
};

export default ListingTable;
